import PropTypes from 'prop-types';
import { Airplane, ArrowRight } from '@phosphor-icons/react';
import { breakpoints, Button, Card, Heading, Inline, PageBlock, Stack, vars } from '@etg/wings';
import { useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import { BrandLogo } from '@eti/components';
import { useEffect, useRef } from 'react';
import ProgressBar from '../../../common/components/ProgressBar';

const blockStyles = css`
  align-content: center;
  background-color: ${vars.colors.pageBackdrop};
  height: 100%;
`;

const cardStyles = css`
  overflow: hidden;
  width: 300px;

  @media (min-width: ${breakpoints._560}) {
    width: 540px;
  }
`;

const lineStyles = css`
  border-left: 1px dotted #8c8c8c;
  height: 15px;

  @media (min-width: ${breakpoints._560}) {
    border-bottom: 1px dotted #8c8c8c;
    border-left: 0;
    flex: 1;
    height: unset;
  }
`;

const subTitleStyles = css`
  font-size: 1.125rem;
`;

const airplaneInlineStyles = css`
  flex: 1;
`;

const airplaneStyles = css`
  transform: rotate(180deg);

  @media (min-width: ${breakpoints._560}) {
    transform: rotate(90deg);

    [dir='rtl'] & {
      transform: rotate(270deg);
    }
  }
`;

const arrowStyles = css`
  [dir='rtl'] & {
    transform: rotate(180deg);
  }
`;

const buttonStyles = css`
  align-self: flex-end;
  display: inline-flex;
  margin-top: 16px;
`;

const logoImage = css`
  height: 20px;
  max-width: 155px;

  @media (min-width: ${breakpoints._560}) {
    height: 30px;
  }
`;

const logosInlineStyles = css`
  gap: 8px;
  justify-content: space-between;

  @media (min-width: ${breakpoints._560}) {
    gap: 24px;
  }
`;

const progressBarStyles = css`
  border-top-left-radius: ${vars.card.borderRadius};
  border-top-right-radius: ${vars.card.borderRadius};

  & > div {
    padding: 0;
    top: -5px;
  }
`;

const stackStyles = css`
  padding: 24px;
`;

const RedirectFromBrandToBrand = ({
  dataTestId,
  fromBrandCode,
  toBrandCode,
  title,
  subtitle,
  toBrandUrl,
}) => {
  const { t } = useTranslation();
  const cardRef = useRef(null);

  useEffect(() => {
    cardRef.current?.focus();
  }, []);

  return (
    <PageBlock as="section" className={blockStyles} data-testid={dataTestId} withPaddingY>
      <Inline align="center" alignY="center">
        <Card
          aria-label={t('ScreenReader.RedirectionPage.Text', fromBrandCode, toBrandCode)}
          className={cardStyles}
          padding={0}
          ref={cardRef}
          tabIndex={-1}
        >
          <Stack className={stackStyles} spacing={{ [breakpoints._0]: 16, [breakpoints._560]: 24 }}>
            <Heading data-testid="redirection-from-brand-to-brand-title" level={1}>
              {title}
            </Heading>
            <div className={subTitleStyles} data-testid="redirection-from-brand-to-brand-subtitle">
              {subtitle}
            </div>
            <Inline alignY="center" className={logosInlineStyles} collapseBelow={breakpoints._560}>
              <BrandLogo
                brand={fromBrandCode}
                className={logoImage}
                data-testid="redirection-from-brand-logo"
                shouldUseContrast
              />
              <Inline
                alignY="center"
                className={airplaneInlineStyles}
                collapseBelow={breakpoints._560}
                spacing={4}
              >
                <div className={lineStyles} />
                <Airplane
                  alt={t('ScreenReader.To.AriaLabel.Text')}
                  className={airplaneStyles}
                  color={vars.colors.decoration.dark}
                  data-testid="redirection-from-brand-to-brand-airplane-icon"
                  size={28}
                  weight="fill"
                />
                <div className={lineStyles} />
              </Inline>
              <BrandLogo
                brand={toBrandCode}
                className={logoImage}
                data-testid="redirection-to-brand-logo"
                shouldUseContrast
              />
            </Inline>
            <Button
              as="a"
              className={buttonStyles}
              data-testid="redirection-from-brand-to-brand-continue-button"
              href={toBrandUrl}
            >
              {t('IbeClient.RedirectCustomerToAnotherSite.Continue.Button')}
              <ArrowRight className={arrowStyles} size={20} weight="light" />
            </Button>
          </Stack>
          <ProgressBar className={progressBarStyles} />
        </Card>
      </Inline>
    </PageBlock>
  );
};

RedirectFromBrandToBrand.propTypes = {
  dataTestId: PropTypes.string,
  fromBrandCode: PropTypes.string,
  subtitle: PropTypes.string,
  title: PropTypes.string,
  toBrandCode: PropTypes.string,
  toBrandUrl: PropTypes.string,
};

export default RedirectFromBrandToBrand;
